import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { useAuth } from '@store/authStore';
import httpClient from '@utils/httpClient';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Snackbar,
    Alert,
    InputAdornment,
    Stack,
    useMediaQuery,
    useTheme,
    Chip,
    Paper,
    Avatar,
    CircularProgress
} from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import { usePermissions } from '@contexts/PermissionContext';
import { useResponsiveUtils } from '@utils/responsiveUtils';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    PictureAsPdf as PDFIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { ChecklistCategory, User, Program, ProgramProgressItem } from '@modules/programManager/types';
import { apiService } from '@modules/programManager/services/apiService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatTableDate } from '@utils/dateFormatter';

interface TaskManagementProps {
    programId?: string;
    programName?: string;
}

type TaskMode = 'Online' | 'In-person' | 'Hybrid' | 'Remote';

const TaskManagement: React.FC<TaskManagementProps> = ({ programId: propProgramId, programName: propProgramName }) => {
    const { programId: routeProgramId } = useParams<{ programId: string }>();
    const programId = routeProgramId || propProgramId || '';
    const programName = propProgramName || 'All Programs';
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const { user } = useAuth();
    const { canEditTask } = usePermissions();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const [programCreatorId, setProgramCreatorId] = useState<string>('');
    const {
        getCardResponsive,
        getSpacingResponsive,
        getTableResponsive
    } = useResponsiveUtils();

    // Consistent Edit/Delete button styles - Same as WorkshopTasksPage
    const editButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
        color: '#FF9800',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'
        },
        '&.Mui-disabled': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
            color: '#FF9800',
            opacity: 0.6
        }
    }), [isDarkMode]);

    const deleteButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
        color: '#F44336',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'
        },
        '&.Mui-disabled': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
            color: '#F44336',
            opacity: 0.6
        }
    }), [isDarkMode]);

    // Status badge component - Memoized for performance
    const getStatusBadge = useCallback((status: string) => {
        const statusConfig = {
            completed: {
                label: 'Completed',
                color: '#4caf50',
                bgColor: isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)'
            },
            'in-progress': {
                label: 'In Progress',
                color: '#ff9800',
                bgColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)'
            },
            pending: {
                label: 'Pending',
                color: isDarkMode ? '#757575' : '#757575',
                bgColor: isDarkMode ? 'rgba(117, 117, 117, 0.2)' : 'rgba(117, 117, 117, 0.1)'
            },
            cancelled: {
                label: 'Cancelled',
                color: '#f44336',
                bgColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)'
            }
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: '24px'
                }}
            />
        );
    }, [isDarkMode]);

    // Helper function to determine if a task is predefined
    const isPredefinedTask = (checklistId: number | string | null, programType: number = 1) => {
        if (!checklistId) return false;
        const id = typeof checklistId === 'string' ? parseInt(checklistId) : checklistId;
        // For sessions: predefined tasks have checklist_id <= 36
        // For workshops: predefined tasks have checklist_id <= 70
        // For new tasks (created by user), they typically have higher IDs or are null
        return programType === 1 ? id <= 36 : id <= 70;
    };

    // Mobile/Tablet card component - Enhanced responsive design - Memoized for performance
    const TaskCard = React.memo(({ task }: { task: ProgramProgressItem }) => (
        <Card
            sx={{
                mb: 2,
                boxShadow: 2,
                borderRadius: 2,
                backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : `1px solid ${theme.palette.divider}`,
                '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                }
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                {/* Header Section - Status + Checkbox + Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Checkbox
                            checked={task.status === 'completed'}
                            indeterminate={task.status === 'in-progress'}
                            onChange={() => {
                                handleTaskStatusChange(task);
                            }}
                            sx={{
                                color: task.status === 'completed' ? '#4caf50' :
                                    task.status === 'in-progress' ? '#ff9800' :
                                        theme.palette.text.secondary,
                                '&.Mui-checked': {
                                    color: '#4caf50',
                                },
                                '&.MuiCheckbox-indeterminate': {
                                    color: '#ff9800',
                                },
                                '&:hover': {
                                    backgroundColor: task.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' :
                                        task.status === 'in-progress' ? 'rgba(255, 152, 0, 0.1)' :
                                            theme.palette.action.hover,
                                }
                            }}
                        />
                        {getStatusBadge(task.status || 'pending')}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={isPredefinedTask(task.checklistId, 1) ? "Cannot edit predefined tasks" : "Edit Task"}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isPredefinedTask(task.checklistId, 1)}
                                    disableRipple
                                    onClick={() => handleEditTask(task)}
                                    sx={editButtonStyles}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isPredefinedTask(task.checklistId, 1) ? "Cannot delete predefined tasks" : "Delete Task"}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={isPredefinedTask(task.checklistId, 1)}
                                    disableRipple
                                    onClick={() => {
                                        setTaskToDelete(task);
                                        setDeleteDialogOpen(true);
                                    }}
                                    sx={deleteButtonStyles}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Task Title */}
                <Typography variant="h6" sx={{
                    fontWeight: 'bold',
                    mb: 1.5,
                    fontSize: '1.1rem',
                    lineHeight: 1.3,
                    color: theme.palette.text.primary
                }}>
                    {task.taskName}
                </Typography>


                {/* Details Section - HORIZONTAL LAYOUT (UNIFORM) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Status Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Status:
                        </Typography>
                        {getStatusBadge(task.status || 'pending')}
                    </Box>

                    {/* Mode Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Mode:
                        </Typography>
                        <Chip
                            label={task.mode || 'Not specified'}
                            size="small"
                            sx={{
                                backgroundColor: task.mode === 'Online' ? '#4CAF50' :
                                    task.mode === 'In-person' ? '#FF9800' :
                                        task.mode === 'Hybrid' ? '#9C27B0' : '#2196F3',
                                color: theme.palette.text.primary,
                                fontWeight: 'bold'
                            }}
                        />
                    </Box>

                    {/* Assigned Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Assigned To:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#2196F3' }}>
                                {(getUserName(task.assignedTo || '') || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                {getUserName(task.assignedTo || '') || 'Unassigned'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Due Date Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Due Date:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                            {task.dueDate ? dayjs(task.dueDate.includes('Z') ? task.dueDate : task.dueDate + 'Z').format('MM/DD/YYYY') : 'No due date'}
                        </Typography>
                    </Box>

                    {/* Completion Date Row */}
                    {task.completionDate && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Completion Date:
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                {dayjs(task.completionDate.includes('Z') ? task.completionDate : task.completionDate + 'Z').format('MM/DD/YYYY')}
                            </Typography>
                        </Box>
                    )}

                    {/* Key Deliverable Row */}
                    {task.keyDeliverable && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Key Deliverable:
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                {task.keyDeliverable}
                            </Typography>
                        </Box>
                    )}

                    {/* Tools Row */}
                    {task.toolsResources && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Tools/Resources:
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                {task.toolsResources}
                            </Typography>
                        </Box>
                    )}

                    {/* Notes Row */}
                    {task.notes && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Notes:
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                {task.notes}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    ));

    // Debug logging
    console.log('TaskManagement - routeProgramId:', routeProgramId);
    console.log('TaskManagement - propProgramId:', propProgramId);
    console.log('TaskManagement - final programId:', programId);
    console.log('TaskManagement - window.location.pathname:', window.location.pathname);
    console.log('TaskManagement - useParams result:', useParams());

    // State management
    const [programProgress, setProgramProgress] = useState<ProgramProgressItem[]>([]);
    const [categories, setCategories] = useState<ChecklistCategory[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [currentProgramId, setCurrentProgramId] = useState<string>(programId);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Snackbar states
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Cache management
    const lastFetchedProgramRef = useRef<string | null>(null);
    const [tasksCache, setTasksCache] = useState<Record<string, any>>({});

    // Dynamic program name and loading states
    const [currentProgramName, setCurrentProgramName] = useState<string>('');
    const [loadingProgramDetails, setLoadingProgramDetails] = useState<boolean>(true);

    // Add Checklist Dialog states
    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
    const [addChecklistLoading, setAddChecklistLoading] = useState<boolean>(false);
    const [addChecklistError, setAddChecklistError] = useState<string>('');

    // React Hook Form for Add Checklist
    const { control: addChecklistControl, handleSubmit: handleAddChecklistSubmit, reset: resetAddChecklistForm, formState: { errors: addChecklistErrors }, watch: watchAddChecklist } = useForm({
        defaultValues: {
            taskName: '',
            toolsResources: '',
            mode: 'Online' as TaskMode,
            keyDeliverable: '',
            assignedTo: '',
            scheduleDate: '',
            completionDate: '',
            notes: ''
        }
    });

    // Filter and search state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [assignedToFilter, setAssignedToFilter] = useState<string>('all');

    // Dialog states
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<ChecklistCategory | null>(null);
    const [taskToDelete, setTaskToDelete] = useState<ProgramProgressItem | null>(null);
    const [editingTask, setEditingTask] = useState<ProgramProgressItem | null>(null);

    // Form states
    const [categoryForm, setCategoryForm] = useState({
        name: '',
        description: '',
        order: 0,
    });

    // React Hook Form for Edit Task
    const { control: editTaskControl, handleSubmit: handleEditTaskSubmit, reset: resetEditTaskForm, formState: { errors: editTaskErrors }, setValue: setEditTaskValue, watch: watchEditTask } = useForm({
        defaultValues: {
            taskName: '',
            assignedTo: '',
            dueDate: '',
            completionDate: '',
            status: 'pending' as 'pending' | 'in-progress' | 'completed',
            notes: '',
            actions: '',
            mode: 'Online' as 'Online' | 'In-person' | 'Hybrid' | 'Remote',
            keyDeliverable: '',
            toolsResources: ''
        }
    });


    const fetchData = useCallback(async () => {
        console.log('TaskManagement - fetchData called - fetching ALL tasks');
        console.log('TaskManagement - Current programId:', programId);
        console.log('TaskManagement - Current programId type:', typeof programId);
        setLoading(true);
        try {
            // Fetch ALL program progress tasks by getting all programs and their progress
            const [programsData, usersData] = await Promise.all([
                apiService.getPrograms(),
                apiService.getUsers(),
            ]);

            // Try to fetch categories, but don't fail if endpoint doesn't exist
            let categoriesData: ChecklistCategory[] = [];
            try {
                categoriesData = await apiService.getChecklistCategories();
                console.log('TaskManagement - Fetched categories:', categoriesData.length);
            } catch (err) {
                console.warn('TaskManagement - Categories endpoint not available, using empty array:', err);
                categoriesData = [];
            }

            console.log('TaskManagement - Fetched programs:', programsData.length);
            console.log('TaskManagement - Fetched users:', usersData.length);

            // Get progress for current program only
            let progressData: ProgramProgressItem[] = [];
            if (currentProgramId) {
                try {
                    console.log(`TaskManagement - Fetching progress for program ID: ${currentProgramId}`);
                    progressData = await apiService.getProgramProgress(currentProgramId.toString());
                    console.log(`Program ${currentProgramId} progress data:`, progressData.length, 'tasks');

                    // Debug: Check for completed tasks
                    const completedTasks = progressData.filter(task => task.status === 'completed');
                    if (completedTasks.length > 0) {
                        console.log(`🔍 Program ${currentProgramId} has ${completedTasks.length} completed tasks:`, completedTasks.map(t => ({ id: t.id, status: t.status, taskName: t.taskName })));
                    }
                } catch (err) {
                    console.error(`Error fetching progress data for program ${currentProgramId}:`, err);
                    console.log(`No progress data for program ${currentProgramId}`);
                }
            } else {
                console.log('TaskManagement - No currentProgramId, skipping progress fetch');
            }

            console.log('TaskManagement - Received progress data:', progressData.length, 'tasks for program', currentProgramId);
            console.log('TaskManagement - Sample task data:', progressData.slice(0, 2).map(task => ({
                id: task.id,
                assignedTo: task.assignedTo,
                dueDate: task.dueDate,
                completionDate: task.completionDate,
                completionDateType: typeof task.completionDate,
                notes: task.notes,
                status: task.status
            })));

            // Debug completion dates specifically
            const tasksWithCompletionDates = progressData.filter(task => task.completionDate);
            console.log('🔍 Tasks with completion dates:', tasksWithCompletionDates.length);
            if (tasksWithCompletionDates.length > 0) {
                console.log('🔍 Sample completion dates:', tasksWithCompletionDates.slice(0, 3).map(task => ({
                    id: task.id,
                    completionDate: task.completionDate,
                    status: task.status
                })));
            }

            // Debug all completion dates for current program
            console.log('🔍 All completion dates for program', currentProgramId, ':', progressData.map(task => ({
                id: task.id,
                completionDate: task.completionDate,
                status: task.status,
                formatted: formatTableDate(task.completionDate)
            })));
            console.log('TaskManagement - Received users data:', usersData.slice(0, 2)); // Log first 2 users
            setProgramProgress(progressData);
            setCategories(categoriesData);
            setUsers(usersData);
            setPrograms(programsData);
            setError(null); // Clear any previous errors on successful fetch
            setLoading(false);

            console.log('🔄 TaskManagement - State updated with fresh data:', progressData.length, 'items');
        } catch (err: any) {
            console.error('TaskManagement - Error fetching data:', err);
            console.error('TaskManagement - Error details:', {
                message: err.message,
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                code: err.code,
                config: err.config
            });

            let errorMessage = 'Failed to load task data from backend. ';
            if (err.response?.status === 401) {
                errorMessage += 'Authentication required. Please login to access data.';
            } else if (err.response?.status === 500) {
                errorMessage += 'Backend server error. Please check server logs.';
            } else if (err.code === 'ECONNREFUSED') {
                errorMessage += 'Backend server not running. Please start the server on port 5000.';
            } else if (err.response?.status === 404) {
                errorMessage += `Program with ID ${currentProgramId} not found.`;
            } else {
                errorMessage += `Backend connection failed: ${err.message}. Please check if server is running on port 5000.`;
            }

            setError(errorMessage);
            setLoading(false);

            // Don't load any data on error - leave arrays empty
            setProgramProgress([]);
            setCategories([]);
            setUsers([]);
            setPrograms([]);
        }
    }, [currentProgramId]); // Add currentProgramId as dependency

    // Fetch program details for dynamic heading
    const fetchProgramDetails = useCallback(async () => {
        if (!currentProgramId) return;

        try {
            setLoadingProgramDetails(true);
            const program = await apiService.getProgramById(currentProgramId);
            setCurrentProgramName(program.title || 'Session Tasks');
            setProgramCreatorId(program.createdById || '');
        } catch (error) {
            console.error('Error fetching program details:', error);
            setCurrentProgramName('Session Tasks');
            setProgramCreatorId('');
        } finally {
            setLoadingProgramDetails(false);
        }
    }, [currentProgramId]);

    // Add Checklist Dialog handlers
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        resetAddChecklistForm();
        setAddChecklistError('');
        setAddChecklistLoading(false);
    };

    const handleAddChecklist = async (data: any) => {
        console.log('🚀 handleAddChecklist called with data:', data);
        console.log('🚀 Current program ID:', currentProgramId);
        console.log('🚀 Form data keys:', Object.keys(data));
        console.log('🚀 Task name:', data.taskName);
        console.log('🚀 All form data:', JSON.stringify(data, null, 2));

        if (!currentProgramId) {
            console.error('❌ No program ID available for creating task');
            setAddChecklistError('Please select a program first');
            return;
        }

        try {
            setAddChecklistLoading(true);
            setAddChecklistError('');

            // Check if currentProgramId is available
            if (!currentProgramId) {
                console.log('❌ No currentProgramId found');
                setAddChecklistError('Please select a program first.');
                setAddChecklistLoading(false);
                return;
            }

            console.log('📝 Creating new task with data:', {
                taskName: data.taskName,
                mode: data.mode,
                assignedTo: data.assignedTo,
                dueDate: data.scheduleDate,
                currentProgramId: currentProgramId
            });

            // Create progress record directly
            const response = await apiService.createProgramProgress(currentProgramId, {
                taskName: data.taskName,
                status: 'pending',
                mode: data.mode,
                assignedTo: data.assignedTo || '',
                dueDate: data.scheduleDate || '',
                completionDate: data.completionDate || '',
                notes: data.notes || '',
                toolsResources: data.toolsResources || '',
                keyDeliverable: data.keyDeliverable || ''
            });

            console.log('✅ Task created successfully:', response);

            setAddChecklistLoading(false);
            handleAddDialogClose();

            // Clear cache and force fresh fetch
            await fetchData();

            setSnackbarMessage('Task added successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('❌ Error adding checklist:', error);
            setAddChecklistLoading(false);
            setAddChecklistError('Failed to add task. Please try again.');
            setSnackbarMessage('An error occurred while adding the task.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Load data on component mount
    useEffect(() => {
        console.log('TaskManagement - useEffect: programId changed to:', programId);
        console.log('TaskManagement - useEffect: programId type:', typeof programId);
        setCurrentProgramId(programId);
    }, [programId]);

    // Fetch program details when currentProgramId changes
    useEffect(() => {
        if (currentProgramId) {
            fetchProgramDetails();
        }
    }, [currentProgramId, fetchProgramDetails]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch data when currentProgramId changes
    useEffect(() => {
        console.log('TaskManagement - currentProgramId useEffect triggered:', currentProgramId);
        if (currentProgramId) {
            console.log('TaskManagement - currentProgramId changed, refreshing data:', currentProgramId);
            fetchData();
        } else {
            console.log('TaskManagement - No currentProgramId, skipping data fetch');
        }
    }, [currentProgramId, fetchData]);

    // Refresh data when component mounts or when programId changes
    useEffect(() => {
        if (programId) {
            console.log('TaskManagement - ProgramId changed, refreshing data:', programId);
            fetchData();
        }
    }, [programId, fetchData]);

    // Debug effect to log programProgress changes
    useEffect(() => {
        console.log('📊 ProgramProgress state updated:', programProgress.length, 'items');
        programProgress.forEach((item, index) => {
            if (item.status === 'completed' || item.status === 'in-progress') {
                console.log(`📊 Item ${index}:`, {
                    id: item.id,
                    status: item.status,
                    taskName: item.taskName
                });
            }
        });
    }, [programProgress]);

    // Filter program progress items based on search and filter criteria
    const filteredTasks = programProgress.filter(item => {
        const matchesSearch = (item.taskName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        const matchesAssignedTo = assignedToFilter === 'all' || item.assignedTo === assignedToFilter;

        return matchesSearch && matchesStatus && matchesAssignedTo;
    });

    // Calculate statistics
    const stats = {
        total: programProgress.length,
        completed: programProgress.filter(item => item.status === 'completed').length,
        inProgress: programProgress.filter(item => item.status === 'in-progress').length,
    };

    // Task status change handler
    const handleTaskStatusChange = (item: ProgramProgressItem) => {
        // Check if programId exists
        if (!item.programId) {
            setError('Task program ID not found');
            return;
        }

        // IMMEDIATE UI UPDATE - Calculate new status first
        let newStatus: 'pending' | 'in-progress' | 'completed' | null;
        let completionDate = item.completionDate;

        // Status progression: pending → in-progress → completed → pending (3 clicks)
        if (!item.status || item.status === 'pending') {
            newStatus = 'in-progress';
            completionDate = item.completionDate; // Keep existing completion date
        } else if (item.status === 'in-progress') {
            newStatus = 'completed';
            // Get today's date in local timezone
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            completionDate = `${year}-${month}-${day}`; // Today's date in local timezone
        } else if (item.status === 'completed') {
            newStatus = 'pending';
            completionDate = null; // Reset completion date
        } else {
            // Default to pending
            newStatus = 'pending';
            completionDate = null;
        }

        console.log('Updating task status:', {
            programId: item.programId,
            taskId: item.id,
            currentStatus: item.status,
            newStatus,
            completionDate
        });

        // IMMEDIATE UI UPDATE - Force immediate state update
        setProgramProgress(prevProgress => {
            const newProgress = prevProgress.map(progressItem => {
                if (progressItem.id === item.id) {
                    const updatedItem = {
                        ...progressItem,
                        status: newStatus,
                        completionDate: completionDate
                    };

                    console.log('🔄 IMMEDIATE State update - Replacing item:', {
                        id: progressItem.id,
                        oldStatus: progressItem.status,
                        newStatus: newStatus,
                        updatedItem: updatedItem
                    });

                    return updatedItem;
                }
                return progressItem;
            });
            return newProgress;
        });

        // Force immediate re-render
        setProgramProgress(prevProgress => prevProgress);

        // BACKGROUND API CALL - Non-blocking (run in background)
        setTimeout(async () => {
            try {
                console.log('🚀 Sending API request with data:', {
                    programId: item.programId,
                    itemId: item.id,
                    status: newStatus,
                    assignedTo: item.assignedTo,
                    dueDate: item.dueDate,
                    completionDate: completionDate,
                    notes: item.notes,
                    actions: item.actions
                });

                const updatedItem = await apiService.updateProgramProgress(
                    item.programId,
                    item.id,
                    {
                        status: newStatus,
                        assignedTo: item.assignedTo, // Preserve existing assignedTo
                        dueDate: item.dueDate, // Preserve existing dueDate
                        completionDate: completionDate,
                        notes: item.notes, // Preserve existing notes
                        actions: item.actions, // Preserve existing actions
                        mode: item.mode, // Preserve existing mode
                        keyDeliverable: item.keyDeliverable, // Preserve existing keyDeliverable
                        toolsResources: item.toolsResources // Preserve existing toolsResources
                    }
                );

                console.log('🎯 API Response received:', updatedItem);

                // Ensure we have the correct status
                const finalStatus = updatedItem.status || newStatus;
                console.log('🔧 Final status to use:', finalStatus);

                // Update the local state with API response - MERGE instead of REPLACE
                setProgramProgress(prevProgress => {
                    const newProgress = prevProgress.map(progressItem => {
                        if (progressItem.id === item.id) {
                            // Merge API response with existing data to preserve all fields
                            const updatedItemWithCorrectStatus = {
                                ...progressItem, // Keep all existing fields
                                ...updatedItem, // Override with API response
                                status: finalStatus, // Ensure correct status
                                id: updatedItem.id.toString(),
                                programId: updatedItem.programId.toString(),
                                // Explicitly preserve critical fields that might be missing in API response
                                assignedTo: updatedItem.assignedTo || progressItem.assignedTo,
                                mode: updatedItem.mode || progressItem.mode,
                                keyDeliverable: updatedItem.keyDeliverable || progressItem.keyDeliverable,
                                toolsResources: updatedItem.toolsResources || progressItem.toolsResources,
                                notes: updatedItem.notes || progressItem.notes,
                                actions: updatedItem.actions || progressItem.actions
                            };

                            console.log('🔄 API State update - Merging item:', {
                                id: progressItem.id,
                                oldStatus: progressItem.status,
                                newStatus: finalStatus,
                                preservedAssignedTo: updatedItemWithCorrectStatus.assignedTo,
                                preservedMode: updatedItemWithCorrectStatus.mode,
                                preservedKeyDeliverable: updatedItemWithCorrectStatus.keyDeliverable,
                                preservedToolsResources: updatedItemWithCorrectStatus.toolsResources
                            });

                            return updatedItemWithCorrectStatus;
                        }
                        return progressItem;
                    });
                    console.log('🔄 API State update - Updated progress length:', newProgress.length);
                    return newProgress;
                });

                console.log('✅ Task status updated successfully:', updatedItem);
                setSuccess(`Task status updated to ${newStatus}!`);

                // Activity logging is handled by the backend API call
                // No need to log separately here to avoid duplicates

                // Dispatch custom event to refresh program list
                console.log('🔄 Dispatching program list refresh event...');
                window.dispatchEvent(new CustomEvent('programStatusUpdated', {
                    detail: {
                        programId: item.programId,
                        newStatus: finalStatus
                    }
                }));
            } catch (err: any) {
                console.error('Error updating task status:', err);
                setError('Failed to update task status. Please try again.');

                // Revert on error
                setProgramProgress(prevProgress => {
                    const newProgress = prevProgress.map(progressItem => {
                        if (progressItem.id === item.id) {
                            return {
                                ...progressItem,
                                status: item.status,
                                completionDate: item.completionDate
                            };
                        }
                        return progressItem;
                    });
                    return newProgress;
                });
            }
        }, 0);
    };


    // Task CRUD operations
    const handleEditTask = (task: ProgramProgressItem) => {
        setEditingTask(task);

        // Convert email to name for assignedTo field
        let assignedToValue = task.assignedTo || '';
        if (assignedToValue && assignedToValue.includes('@')) {
            // If it's an email, try to find the corresponding name
            const user = users.find(u => u.email === assignedToValue);
            if (user) {
                assignedToValue = user.name;
            } else {
                // If no user found, try to extract name from email
                assignedToValue = assignedToValue.split('@')[0];
            }
        }

        // Set form values using React Hook Form
        setEditTaskValue('taskName', task.taskName || '');
        setEditTaskValue('assignedTo', assignedToValue);
        setEditTaskValue('dueDate', task.dueDate || '');
        setEditTaskValue('completionDate', task.completionDate || '');
        setEditTaskValue('status', (task.status as 'pending' | 'in-progress' | 'completed') || 'pending');
        setEditTaskValue('notes', task.notes || '');
        setEditTaskValue('actions', typeof task.actions === 'string' ? task.actions : '');
        setEditTaskValue('mode', (task.mode as 'Online' | 'In-person' | 'Hybrid' | 'Remote') || 'Online');
        setEditTaskValue('keyDeliverable', task.keyDeliverable || '');
        setEditTaskValue('toolsResources', task.toolsResources || '');

        setEditTaskDialogOpen(true);
    };

    const handleUpdateTask = async (data: any) => {
        console.log('🚀 handleUpdateTask called with data:', data);
        console.log('🚀 editingTask:', editingTask);
        console.log('🚀 Form data keys:', Object.keys(data));
        console.log('🚀 All form data:', JSON.stringify(data, null, 2));

        if (!editingTask) {
            console.log('❌ No editing task found');
            return;
        }

        // Use the task's own programId instead of currentProgramId
        const taskProgramId = editingTask.programId;
        console.log('🚀 taskProgramId:', taskProgramId);

        if (!taskProgramId) {
            console.log('❌ Task program ID not found');
            setError('Task program ID not found');
            return;
        }

        try {
            const updateData = {
                taskName: data.taskName, // Fixed: use correct field name
                assignedTo: data.assignedTo,
                dueDate: data.dueDate,
                completionDate: data.completionDate,
                status: data.status,
                notes: data.notes,
                actions: data.actions,
                mode: data.mode,
                keyDeliverable: data.keyDeliverable,
                toolsResources: data.toolsResources
            };

            console.log('Updating task with data:', updateData);
            console.log('Program ID:', taskProgramId);
            console.log('Task ID:', editingTask.id);

            await apiService.updateProgramProgress(taskProgramId, editingTask.id, updateData);

            console.log('✅ Task updated successfully, updating UI...');

            // IMMEDIATE UI UPDATE - Update the specific task in the state
            setProgramProgress(prevProgress => {
                const updatedProgress = prevProgress.map(item => {
                    if (item.id === editingTask.id) {
                        const updatedItem = {
                            ...item,
                            ...updateData,
                            // Ensure we have the correct status
                            status: updateData.status || item.status,
                            // Update task name if changed
                            taskName: updateData.taskName || item.taskName
                        };
                        console.log('🔄 TaskManagement - Updated task in state:', editingTask.id, 'New status:', updatedItem.status);
                        return updatedItem;
                    }
                    return item;
                });
                console.log('🔄 TaskManagement - Updated task in state:', editingTask.id);
                return updatedProgress;
            });

            // Force refresh data from server to ensure consistency
            setTimeout(() => {
            fetchData();
            }, 100);

            // Close dialog and reset state
            setEditTaskDialogOpen(false);
            setEditingTask(null);
            resetEditTaskForm();

            // Show success message
            setSnackbarMessage('Task updated successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error: any) {
            console.error('Error updating task:', error);
            console.error('Error response:', error.response);
            console.error('Error details:', error.response?.data || error.message);
            setError(`Failed to update task: ${error.response?.data?.error || error.message || 'Unknown error'}`);
        }
    };

    const handleDeleteTask = async () => {
        if (!taskToDelete) return;

        const taskProgramId = taskToDelete.programId;
        if (!taskProgramId || taskProgramId.trim() === '') {
            setError('Task does not have a valid program ID.');
            return;
        }

        try {
            await apiService.deleteProgramProgress(taskProgramId, taskToDelete.id);
            setProgramProgress(programProgress.filter(item => item.id !== taskToDelete.id));
            setSuccess('Task deleted successfully!');
            setDeleteDialogOpen(false);
            setTaskToDelete(null);
        } catch (err: any) {
            console.error('Error deleting task:', err);
            setError('Failed to delete task. Please try again.');
        }
    };

    // Category CRUD operations
    const handleCreateCategory = async () => {
        try {
            const newCategory = await apiService.createChecklistCategory({
                name: categoryForm.name,
                description: categoryForm.description,
                order: categoryForm.order
            });
            setCategories([...categories, newCategory]);
            setCategoryDialogOpen(false);
            resetCategoryForm();
            setSuccess('Category created successfully!');
        } catch (err) {
            setError('Failed to create category. Please try again.');
        }
    };

    const handleUpdateCategory = async () => {
        if (!editingCategory) return;

        try {
            const updatedCategory = await apiService.updateChecklistCategory(
                editingCategory.type_id.toString(),
                {
                    name: categoryForm.name,
                    description: categoryForm.description,
                    order: categoryForm.order
                }
            );
            setCategories(categories.map(cat => cat.type_id === editingCategory.type_id ? updatedCategory : cat));
            setCategoryDialogOpen(false);
            setEditingCategory(null);
            resetCategoryForm();
            setSuccess('Category updated successfully!');
        } catch (err) {
            setError('Failed to update category. Please try again.');
        }
    };


    // Export/Import
    const handleExport = async (format: 'csv' | 'pdf') => {
        if (!currentProgramId || currentProgramId === '') {
            setError('Please select a program first to export tasks.');
            return;
        }
        try {
            if (format === 'pdf') {
                console.log('📄 Exporting tasks as PDF...');
                console.log('jsPDF available:', typeof jsPDF !== 'undefined');
                console.log('autoTable available:', typeof autoTable !== 'undefined');
                console.log('Program progress data:', programProgress.length, 'tasks');

                // Check if jsPDF is available
                if (typeof jsPDF === 'undefined') {
                    throw new Error('jsPDF is not available. Please refresh the page and try again.');
                }

                // Check if autoTable is available
                if (typeof autoTable === 'undefined') {
                    throw new Error('autoTable is not available. Please refresh the page and try again.');
                }

                // Handle PDF export with jsPDF
                const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation

                // Add title
                doc.setFontSize(20);
                doc.text(`${currentProgramName || 'Session Tasks'} - Tasks Report`, 14, 22);

                // Add generation date and total count
                doc.setFontSize(12);
                doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);
                doc.text(`Total Tasks: ${programProgress.length}`, 14, 38);

                // Prepare table data
                const tableData = programProgress.map(item => {
                    // Format status with visual indicators like table
                    let statusText = 'Pending';
                    if (item.status === 'completed') {
                        statusText = 'Completed';
                    } else if (item.status === 'in-progress') {
                        statusText = 'In Progress';
                    } else if (item.status === 'pending') {
                        statusText = 'Pending';
                    } else if (item.status === 'cancelled') {
                        statusText = 'Cancelled';
                    }

                    return [
                        statusText,
                        item.taskName || 'No task name',
                        item.toolsResources || 'No tools/resources',
                        item.mode || 'Not specified',
                        item.keyDeliverable || 'No key deliverable',
                        item.assignedTo || 'Unassigned',
                        formatTableDate(item.dueDate),
                        formatTableDate(item.completionDate),
                        formatTableDate(item.createdAt),
                        formatTableDate(item.updatedAt),
                        item.notes || 'No notes'
                    ];
                });

                // Add table using autoTable
                console.log('Adding table to PDF...');
                console.log('autoTable function:', autoTable);
                console.log('doc object:', doc);
                try {
                    autoTable(doc, {
                        head: [['Status', 'Task Name', 'Tools/Resources', 'Mode', 'Key Deliverable', 'Assigned To', 'Due Date', 'Completion Date', 'Created At', 'Updated At', 'Notes']],
                        body: tableData,
                        startY: 45,
                        styles: {
                            fontSize: 7,
                            cellPadding: 2,
                        },
                        headStyles: {
                            fillColor: [26, 43, 69], // Dark blue header
                            textColor: 255, // White text
                            fontStyle: 'bold',
                        },
                        alternateRowStyles: {
                            fillColor: [245, 245, 245], // Light gray for alternate rows
                        },
                        columnStyles: {
                            0: { cellWidth: 20, halign: 'left', fontStyle: 'bold', fontSize: 8 }, // Status - Left aligned, Bold, Larger font
                            1: { cellWidth: 30 }, // Task Name
                            2: { cellWidth: 25 }, // Tools/Resources
                            3: { cellWidth: 15 }, // Mode
                            4: { cellWidth: 25 }, // Key Deliverable
                            5: { cellWidth: 20 }, // Assigned To
                            6: { cellWidth: 18 }, // Due Date
                            7: { cellWidth: 20 }, // Completion Date
                            8: { cellWidth: 18 }, // Created At
                            9: { cellWidth: 18 }, // Updated At
                            10: { cellWidth: 25 } // Notes
                        },
                        willDrawCell: (data: any) => {
                            // Apply colors to Status column (column 0) before drawing
                            if (data.column.index === 0) {
                                console.log('Session PDF willDrawCell - Full data object:', data);
                                console.log('Session PDF willDrawCell - Cell object:', data.cell);
                                console.log('Session PDF willDrawCell - Text array:', data.cell.text);

                                // Get the full text from the cell
                                let statusText = '';
                                if (Array.isArray(data.cell.text)) {
                                    statusText = data.cell.text.join('');
                                } else if (typeof data.cell.text === 'string') {
                                    statusText = data.cell.text;
                                } else if (data.cell.text && data.cell.text[0]) {
                                    statusText = data.cell.text[0];
                                }

                                console.log('Session PDF willDrawCell - Final status text:', statusText);

                                // Apply colors based on status text
                                if (statusText && statusText.includes('Completed')) {
                                    console.log('Session PDF willDrawCell - Found Completed, applying green');
                                    doc.setTextColor(0, 128, 0); // Green
                                } else if (statusText && (statusText.includes('In Progress') || statusText.includes('InProgress'))) {
                                    console.log('Session PDF willDrawCell - Found In Progress, applying orange');
                                    doc.setTextColor(255, 140, 0); // Orange
                                } else if (statusText && statusText.includes('Pending')) {
                                    console.log('Session PDF willDrawCell - Found Pending, applying black');
                                    doc.setTextColor(0, 0, 0); // Black
                                } else if (statusText && statusText.includes('Cancelled')) {
                                    console.log('Session PDF willDrawCell - Found Cancelled, applying red');
                                    doc.setTextColor(255, 0, 0); // Red
                                } else {
                                    console.log('Session PDF willDrawCell - No match found, using default black');
                                    doc.setTextColor(0, 0, 0); // Default black
                                }
                            }
                        },
                        margin: { top: 45, left: 14, right: 14 },
                    });
                    console.log('Table added successfully');
                } catch (tableError) {
                    console.error('Error adding table:', tableError);
                    // Fallback: add simple text table
                    doc.setFontSize(10);
                    doc.text('Task Name | Tools/Resources | Mode | Key Deliverable | Assigned To | Due Date | Completion Date | Created At | Updated At | Notes', 14, 50);
                    let yPos = 60;
                    tableData.forEach((row, index) => {
                        if (yPos > 280) { // Start new page if needed
                            doc.addPage();
                            yPos = 20;
                        }
                        doc.text(row.join(' | '), 14, yPos);
                        yPos += 10;
                    });
                }

                // Add footer
                const pageCount = doc.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.text(`Page ${i} of ${pageCount}`, 14, doc.internal.pageSize.height - 10);
                    doc.text('Nucleus Control Panel', doc.internal.pageSize.width - 14 - 50, doc.internal.pageSize.height - 10);
                }

                // Save the PDF
                doc.save(`${currentProgramName || 'Session_Tasks'}_tasks.pdf`);
                setSuccess('Tasks exported to PDF successfully!');
            } else {
                // Handle CSV export
                const blob = await apiService.exportChecklist(currentProgramId, format);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${currentProgramName || 'Session_Tasks'}_tasks.${format}`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                setSuccess(`Tasks exported to ${format.toUpperCase()} successfully!`);
            }
        } catch (err) {
            console.error('Export error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(`Failed to export tasks: ${errorMessage}. Please try again.`);
        }
    };

    const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!currentProgramId || currentProgramId === '') {
            setError('Please select a program first to import tasks.');
            return;
        }

        try {
            const result = await apiService.importChecklist(currentProgramId, file);
            await fetchData(); // Refresh data
            setSuccess(`Imported ${result.imported} tasks successfully!`);
        } catch (err) {
            setError('Failed to import tasks. Please try again.');
        }

        // Reset file input
        event.target.value = '';
    };

    // Helper functions


    const resetCategoryForm = () => {
        setCategoryForm({
            name: '',
            description: '',
            order: 0,
        });
    };

    const resetTaskForm = () => {
        resetEditTaskForm();
    };



    const getUserName = (userEmailOrId: string) => {
        if (!userEmailOrId) return 'Unassigned';
        // Try to find by email first, then by id
        const user = users.find(u => u.email === userEmailOrId || u.id === userEmailOrId);
        return user?.name || userEmailOrId; // Return email if user not found, better than 'Unassigned'
    };


    return (
        <Box sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            p: { xs: 2, sm: 3 },
            backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
            borderRadius: '8px',
            margin: { xs: '8px 1px', sm: '16px 2px' },
            minHeight: 'calc(100vh - 64px)',
            boxSizing: 'border-box',
            position: 'relative',
            // Ensure proper spacing on all sides
            '@media (max-width: 600px)': {
                margin: '8px 1px',
                p: 2
            }
        }}>
            {/* Program Header */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="h4" component="h1" sx={{
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {loadingProgramDetails ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                                Loading...
                            </Box>
                        ) : (
                            currentProgramName || 'Session Tasks'
                        )}
                    </Typography>
                </Box>

                {/* Program Selector */}
                {programs.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel sx={{
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                '&.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
                                },
                            }}>
                                Select Program
                            </InputLabel>
                            <Select
                                value={currentProgramId || ''}
                                onChange={(e) => setCurrentProgramId(e.target.value)}
                                label="Select Program"
                                displayEmpty
                                sx={{
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.action.hover,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                    },
                                    '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
                                    '& .MuiSelect-select': {
                                        backgroundColor: 'transparent',
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                            color: theme.palette.text.primary,
                                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${theme.palette.divider}`,
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                                            '& .MuiMenuItem-root': {
                                                color: theme.palette.text.primary,
                                                '&:hover': {
                                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="" disabled>
                                    Select a Program
                                </MenuItem>
                                {programs.map((program) => (
                                    <MenuItem key={program.id} value={program.id}>
                                        {program.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {!currentProgramId && programs.length === 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.dark', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                            ⚠️ No programs available. Please create a program first.
                        </Typography>
                    </Box>
                )}

            </Box>

            {/* Summary Cards */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3
            }}>
                <Box sx={{ flex: 1 }}>
                    <Card sx={{
                        width: '100%',
                        height: { xs: '120px', sm: '160px' },
                        background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.04) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode
                            ? '1px solid rgba(33, 150, 243, 0.3)'
                            : '1px solid rgba(33, 150, 243, 0.2)',
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                        },
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" component="div" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                    }}>
                                        {stats.total}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}>
                                        Total Tasks
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    borderRadius: '50%',
                                    background: isDarkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Typography variant="h6" sx={{
                                        color: theme.palette.text.primary,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>📋</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Card sx={{
                        width: '100%',
                        height: { xs: '120px', sm: '160px' },
                        background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.04) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode
                            ? '1px solid rgba(76, 175, 80, 0.3)'
                            : '1px solid rgba(76, 175, 80, 0.2)',
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
                        },
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" component="div" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                    }}>
                                        {stats.completed}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}>
                                        Completed
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    borderRadius: '50%',
                                    background: isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Typography variant="h6" sx={{
                                        color: theme.palette.text.primary,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>✅</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Card sx={{
                        width: '100%',
                        height: { xs: '120px', sm: '160px' },
                        background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.04) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode
                            ? '1px solid rgba(255, 152, 0, 0.3)'
                            : '1px solid rgba(255, 152, 0, 0.2)',
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
                        },
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" component="div" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.8rem', sm: '2.125rem' }
                                    }}>
                                        {stats.inProgress}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}>
                                        In Progress
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    borderRadius: '50%',
                                    background: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Typography variant="h6" sx={{
                                        color: theme.palette.text.primary,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>⏳</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Filters and Actions */}
            <Card sx={{
                mb: 3,
                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
            }}>
                <CardContent>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: { xs: 'stretch', sm: 'center' }
                    }}>
                        <Box sx={{ flex: { xs: 1, sm: 3 } }}>
                            <TextField
                                fullWidth
                                label="Search tasks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                        '&.Mui-focused': {
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: { xs: 1, sm: 2 } }}>
                            <FormControl fullWidth>
                                <InputLabel sx={{
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
                                    },
                                }}>
                                    Status
                                </InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label="Status"
                                    displayEmpty
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                        },
                                        '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
                                    }}
                                >
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="in-progress">In Progress</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: { xs: 1, sm: 2 } }}>
                            <FormControl fullWidth>
                                <InputLabel sx={{
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
                                    },
                                }}>
                                    Users
                                </InputLabel>
                                <Select
                                    value={assignedToFilter}
                                    onChange={(e) => setAssignedToFilter(e.target.value)}
                                    label="Users"
                                    displayEmpty
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                        },
                                        '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
                                    }}
                                >
                                    <MenuItem value="all">All Users</MenuItem>
                                    {users.map(user => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', sm: 'flex-start' },
                            width: { xs: '100%', sm: 'auto' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    console.log('🚀 Add Checklist button clicked');
                                    console.log('🚀 Current openAddDialog state:', openAddDialog);
                                    setOpenAddDialog(true);
                                    console.log('🚀 Setting openAddDialog to true');
                                }}
                                sx={{
                                    backgroundColor: '#2196F3',
                                    minWidth: { xs: '100%', sm: 'auto' },
                                    height: { xs: '48px', sm: '56px' },
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    px: 3,
                                    '&:hover': {
                                        backgroundColor: '#1976D2',
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98)',
                                        transition: 'transform 0.1s ease',
                                    },
                                    // Touch-friendly interactions
                                    '@media (hover: none) and (pointer: coarse)': {
                                        '&:hover': {
                                            backgroundColor: '#2196F3',
                                        },
                                        '&:active': {
                                            backgroundColor: '#1976D2',
                                            transform: 'scale(0.95)',
                                        },
                                    },
                                }}
                            >
                                Add Checklist
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                    console.log('🔄 Task Management refresh button clicked');
                                    window.location.reload();
                                }}
                                sx={{
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                    height: { xs: '48px', sm: '56px' },
                                    px: 3,
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>


            {/* Mobile Card Layout */}
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                            Loading tasks...
                        </Typography>
                    </Box>
                ) : programProgress.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                            No tasks found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Try adjusting your filters or check back later.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ px: 1 }}>
                        {programProgress.map((task) => (
                            <TaskCard key={task.id} task={task} />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Desktop Table */}
            <Card sx={{
                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                display: { xs: 'none', lg: 'block' },
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box'
            }}>
                <TableContainer sx={{
                    overflow: 'auto',
                    maxHeight: { xs: '70vh', sm: 'none' },
                    width: '100%',
                    maxWidth: '100%',
                    // Prevent horizontal scroll issues
                    '&::-webkit-scrollbar': {
                        height: '8px',
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: isDarkMode
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode
                            ? 'rgba(255, 255, 255, 0.3)'
                            : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: isDarkMode
                                ? 'rgba(255, 255, 255, 0.5)'
                                : 'rgba(0, 0, 0, 0.3)',
                        },
                    },
                }}>
                    <Table sx={{
                        width: '100%',
                        minWidth: { xs: '100%', sm: '100%', md: '100%', lg: '100%' },
                        tableLayout: 'auto',
                        '& .MuiTableCell-root': {
                            padding: {
                                xs: '8px 4px',
                                sm: '6px 4px',
                                md: '8px 6px',
                                lg: '10px 8px',
                                xl: '12px 10px'
                            },
                            fontSize: {
                                xs: '0.7rem',
                                sm: '0.65rem',
                                md: '0.7rem',
                                lg: '0.75rem',
                                xl: '0.8rem'
                            },
                            whiteSpace: { xs: 'normal', sm: 'normal', md: 'nowrap' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: { xs: 1.2, sm: 1.2, md: 1.3, lg: 1.4 },
                            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                            borderRight: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                        }
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" sx={{
                                    width: 50,
                                    minWidth: 50,
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: isDarkMode ? '#1a1a2e' : 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: isDarkMode ? 'none' : 'blur(10px)',
                                    zIndex: 20,
                                    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                                    boxShadow: isDarkMode ? '2px 0 8px rgba(0, 0, 0, 0.3)' : '2px 0 8px rgba(0, 0, 0, 0.1)'
                                }}></TableCell>
                                <TableCell sx={{
                                    width: { xs: '25%', sm: '20%', md: '18%', lg: '16%' },
                                    minWidth: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
                                    display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                }}>Task Name</TableCell>
                                <TableCell sx={{
                                    width: { xs: '0%', sm: '15%', md: '14%', lg: '13%' },
                                    minWidth: { xs: '0px', sm: '100px', md: '120px', lg: '140px' },
                                    display: { xs: 'none', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                }}>Tools/Resources</TableCell>
                                <TableCell sx={{
                                    width: { xs: '15%', sm: '12%', md: '10%', lg: '8%' },
                                    minWidth: { xs: '60px', sm: '70px', md: '80px', lg: '90px' },
                                    display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                }}>Mode</TableCell>
                                <TableCell sx={{
                                    width: { xs: '0%', sm: '0%', md: '15%', lg: '14%' },
                                    minWidth: { xs: '0px', sm: '0px', md: '120px', lg: '140px' },
                                    display: { xs: 'none', sm: 'none', md: 'table-cell', lg: 'table-cell' }
                                }}>Key Deliverable</TableCell>
                                <TableCell sx={{
                                    width: { xs: '20%', sm: '18%', md: '15%', lg: '13%' },
                                    minWidth: { xs: '80px', sm: '90px', md: '100px', lg: '110px' },
                                    display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                }}>Assigned To</TableCell>
                                <TableCell sx={{
                                    width: { xs: '0%', sm: '0%', md: '12%', lg: '11%' },
                                    minWidth: { xs: '0px', sm: '0px', md: '100px', lg: '120px' },
                                    display: { xs: 'none', sm: 'none', md: 'table-cell', lg: 'table-cell' }
                                }}>Schedule Date</TableCell>
                                <TableCell sx={{
                                    width: { xs: '0%', sm: '0%', md: '12%', lg: '11%' },
                                    minWidth: { xs: '0px', sm: '0px', md: '100px', lg: '120px' },
                                    display: { xs: 'none', sm: 'none', md: 'table-cell', lg: 'table-cell' }
                                }}>Completion Date</TableCell>
                                <TableCell sx={{
                                    width: { xs: '0%', sm: '0%', md: '0%', lg: '10%' },
                                    minWidth: { xs: '0px', sm: '0px', md: '0px', lg: '100px' },
                                    display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                }}>Created At</TableCell>
                                <TableCell sx={{
                                    width: { xs: '0%', sm: '0%', md: '0%', lg: '10%' },
                                    minWidth: { xs: '0px', sm: '0px', md: '0px', lg: '100px' },
                                    display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                }}>Updated At</TableCell>
                                <TableCell sx={{
                                    width: { xs: '100px', sm: '120px', md: '140px', lg: '160px' },
                                    minWidth: { xs: '100px', sm: '120px', md: '140px', lg: '160px' },
                                    maxWidth: { xs: '100px', sm: '120px', md: '140px', lg: '160px' },
                                    display: { xs: 'none', sm: 'none', md: 'table-cell', lg: 'table-cell' }
                                }}>Notes</TableCell>
                                <TableCell sx={{
                                    width: '120px',
                                    minWidth: '120px',
                                    maxWidth: '120px',
                                    position: 'sticky',
                                    right: 0,
                                    backgroundColor: isDarkMode ? '#1a1a2e' : '#ffffff',
                                    zIndex: 21,
                                    borderLeft: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'}`
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                                            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                                                Loading tasks...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : filteredTasks.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                                            No tasks found
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            {searchTerm || statusFilter !== 'all' || assignedToFilter !== 'all'
                                                ? 'Try adjusting your search or filter criteria'
                                                : 'No tasks have been created for this session yet'
                                            }
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredTasks.map((item, index) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell padding="checkbox" sx={{
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: isDarkMode ? '#1a1a2e' : 'rgba(255, 255, 255, 0.95)',
                                            backdropFilter: isDarkMode ? 'none' : 'blur(10px)',
                                            zIndex: 20,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                                            boxShadow: isDarkMode ? '2px 0 8px rgba(0, 0, 0, 0.3)' : '2px 0 8px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            <Checkbox
                                                checked={item.status === 'completed'}
                                                indeterminate={item.status === 'in-progress'}
                                                onChange={() => handleTaskStatusChange(item)}
                                                sx={{
                                                    color: item.status === 'completed' ? '#4caf50' : // Green for completed
                                                        item.status === 'in-progress' ? '#ff9800' : // Orange for in-progress
                                                            theme.palette.text.secondary, // Theme color for pending
                                                    '&.Mui-checked': {
                                                        color: '#4caf50', // Green when checked
                                                    },
                                                    '&.MuiCheckbox-indeterminate': {
                                                        color: '#ff9800', // Orange when indeterminate
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: item.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' :
                                                            item.status === 'in-progress' ? 'rgba(255, 152, 0, 0.1)' :
                                                                theme.palette.action.hover, // Theme hover for pending
                                                    }
                                                }}
                                                onClick={() => {
                                                    console.log('🔘 Checkbox clicked for task:', item.id);
                                                    console.log('🔘 Current status:', item.status);
                                                    console.log('🔘 Should be checked:', item.status === 'completed');
                                                    console.log('🔘 Should be indeterminate:', item.status === 'in-progress');
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{
                                            width: { xs: '25%', sm: '20%', md: '18%', lg: '16%' },
                                            minWidth: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
                                            position: { xs: 'sticky', sm: 'static' },
                                            left: { xs: 50, sm: 'auto' },
                                            backgroundColor: { xs: isDarkMode ? '#1a1a2e' : theme.palette.background.paper, sm: 'transparent' },
                                            zIndex: { xs: 1, sm: 'auto' }
                                        }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    textDecoration: item.status === 'completed' ? 'line-through' : 'none',
                                                    opacity: item.status === 'completed' ? 0.7 : 1,
                                                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    color: theme.palette.text.primary
                                                }}
                                            >
                                                {item.taskName}
                                            </Typography>
                                            {(item.status === 'in-progress' || item.status === 'completed') && (
                                                <Typography
                                                    variant="caption"
                                                    color={
                                                        item.status === 'completed' ? '#4caf50' : // Green for completed
                                                            '#ff9800' // Orange for in-progress
                                                    }
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase',
                                                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                                        display: 'block',
                                                        marginTop: 0.5
                                                    }}
                                                >
                                                    {item.status === 'in-progress' ? 'IN PROGRESS' : 'COMPLETED'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '150px',
                                            minWidth: '150px',
                                            maxWidth: '150px',
                                            display: { md: 'table-cell', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                color: theme.palette.text.secondary
                                            }}>
                                                {item.toolsResources || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '80px',
                                            minWidth: '80px',
                                            maxWidth: '80px',
                                            display: { md: 'table-cell', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                color: theme.palette.text.secondary
                                            }}>
                                                {item.mode || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '150px',
                                            minWidth: '150px',
                                            maxWidth: '150px',
                                            display: { md: 'none', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                color: theme.palette.text.primary
                                            }}>
                                                {item.keyDeliverable || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '120px',
                                            minWidth: '120px',
                                            maxWidth: '120px',
                                            display: { md: 'table-cell', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                color: theme.palette.text.primary
                                            }}>
                                                {item.assignedTo ? getUserName(item.assignedTo) : 'Unassigned'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '140px',
                                            minWidth: '140px',
                                            maxWidth: '140px',
                                            display: { md: 'none', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: theme.palette.text.secondary
                                            }}>
                                                {formatTableDate(item.dueDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '140px',
                                            minWidth: '140px',
                                            maxWidth: '140px',
                                            display: { md: 'none', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: theme.palette.text.secondary
                                            }}>
                                                {formatTableDate(item.completionDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '140px',
                                            minWidth: '140px',
                                            maxWidth: '140px',
                                            display: { md: 'none', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: theme.palette.text.secondary
                                            }}>
                                                {formatTableDate(item.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '140px',
                                            minWidth: '140px',
                                            maxWidth: '140px',
                                            display: { md: 'none', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: theme.palette.text.secondary
                                            }}>
                                                {formatTableDate(item.updatedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '120px',
                                            minWidth: '120px',
                                            maxWidth: '120px',
                                            display: { md: 'none', lg: 'table-cell' },
                                            fontSize: { md: '0.7rem', lg: '0.8rem' }
                                        }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    color: theme.palette.text.secondary
                                                }}
                                            >
                                                {item.notes || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            width: '120px',
                                            minWidth: '120px',
                                            maxWidth: '120px',
                                            position: 'sticky',
                                            right: 0,
                                            backgroundColor: isDarkMode ? '#1a1a2e' : '#ffffff',
                                            zIndex: 20,
                                            borderLeft: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'}`,
                                            padding: '12px 16px',
                                            verticalAlign: 'middle'
                                        }}>
                                            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                                                <Tooltip title={isPredefinedTask(item.checklistId, 1) ? "Cannot edit predefined tasks" : "Edit Task"}>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isPredefinedTask(item.checklistId, 1)}
                                                        disableRipple
                                                        onClick={() => handleEditTask(item)}
                                                        sx={editButtonStyles}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isPredefinedTask(item.checklistId, 1) ? "Cannot delete predefined tasks" : "Delete Task"}>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isPredefinedTask(item.checklistId, 1)}
                                                        disableRipple
                                                        onClick={() => {
                                                            setTaskToDelete(item);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        sx={deleteButtonStyles}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>


            {/* Category Dialog */}
            <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Create New Category'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                                <TextField
                                    fullWidth
                                    label="Category Name"
                                    value={categoryForm.name}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                    required
                                />
                            </Box>
                            <Box sx={{ flex: '1 1 150px', minWidth: '100px' }}>
                                <TextField
                                    fullWidth
                                    label="Order"
                                    type="number"
                                    value={categoryForm.order}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                                />
                            </Box>
                        </Box>
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={3}
                            value={categoryForm.description}
                            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCategoryDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                            '&:hover': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                        variant="contained"
                    >
                        {editingCategory ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    Delete Task
                </DialogTitle>
                <DialogContent sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary
                }}>
                    <Typography sx={{ mt: 2 }}>
                        Are you sure you want to delete "{taskToDelete?.taskName}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px'
                }}>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setTaskToDelete(null);
                        }}
                        variant="outlined"
                        sx={{
                            color: theme.palette.text.primary,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1.5,
                            '&:hover': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                borderWidth: 1.5
                            },
                            '&:focus': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                outline: 'none'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteTask}
                        variant="contained"
                        color="error"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Task Dialog */}
            <Dialog
                open={editTaskDialogOpen}
                onClose={() => setEditTaskDialogOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        maxHeight: 'calc(100vh - 120px)',
                        marginTop: '80px',
                        marginBottom: '40px',
                        marginLeft: '32px',
                        marginRight: '32px'
                    }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: '1.25rem',
                    fontWeight: 'bold'
                }}>
                    ✏️ Edit Task Details
                </DialogTitle>
                <Box component="form" onSubmit={handleEditTaskSubmit(handleUpdateTask)}>
                <DialogContent sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}>
                    <Typography variant="body2" sx={{
                        color: theme.palette.text.secondary,
                        mb: 2,
                        mt: 2,
                        fontStyle: 'italic'
                    }}>
                        Update the task details below:
                    </Typography>
                        <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                        <Controller
                            name="taskName"
                            control={editTaskControl}
                            rules={{ required: 'Task name is required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Task Name"
                                    fullWidth
                                    variant="outlined"
                                    disabled={!!(editingTask && editingTask.checklistId && parseInt(editingTask.checklistId) <= 36)}
                                    InputLabelProps={{ shrink: true }}
                                    error={!!editTaskErrors.taskName}
                                    helperText={editTaskErrors.taskName?.message}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="assignedTo"
                            control={editTaskControl}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel
                                        shrink={true}
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                                backgroundColor: theme.palette.background.default,
                                                padding: '0 8px',
                                            },
                                        }}
                                    >
                                        Assigned To
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        label="Assigned To"
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em style={{ color: theme.palette.text.secondary }}>Select a user</em>;
                                            }
                                            return selected;
                                        }}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme.palette.background.default,
                                                    color: theme.palette.text.primary,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme.palette.text.primary,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.action.hover,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Select a user</em>
                                        </MenuItem>
                                        {users.map((user) => (
                                            <MenuItem key={user.id} value={user.name}>
                                                {user.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name="dueDate"
                            control={editTaskControl}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Schedule Date"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                            cursor: 'pointer',
                                            opacity: 0.7,
                                            '&:hover': {
                                                opacity: 1
                                            }
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="completionDate"
                            control={editTaskControl}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Completion Date"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                            cursor: 'pointer',
                                            opacity: 0.7,
                                            '&:hover': {
                                                opacity: 1
                                            }
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="mode"
                            control={editTaskControl}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel
                                        shrink={true}
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)',
                                                backgroundColor: theme.palette.background.default,
                                                padding: '0 8px',
                                            },
                                        }}
                                    >
                                        Mode
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        label="Mode"
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em style={{ color: theme.palette.text.secondary }}>Select mode</em>;
                                            }
                                            return selected;
                                        }}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme.palette.background.default,
                                                    color: theme.palette.text.primary,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme.palette.text.primary,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.action.hover,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="Online">Online</MenuItem>
                                        <MenuItem value="In-person">In-person</MenuItem>
                                        <MenuItem value="Hybrid">Hybrid</MenuItem>
                                        <MenuItem value="Remote">Remote</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name="keyDeliverable"
                            control={editTaskControl}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Key Deliverable"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="toolsResources"
                            control={editTaskControl}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Tools/Resources"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="notes"
                            control={editTaskControl}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Notes"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px'
                }}>
                    <Button
                        onClick={() => setEditTaskDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            color: theme.palette.text.primary,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1.5,
                            '&:hover': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                borderWidth: 1.5
                            },
                            '&:focus': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                outline: 'none'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                            onClick={(e) => {
                                console.log('🖱️ TaskManagement Update button clicked!');
                                console.log('🖱️ editingTask:', editingTask);
                                console.log('🖱️ Form validation errors:', editTaskErrors);
                            }}
                        sx={{
                            backgroundColor: '#2196F3',
                            color: '#ffffff',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#1976D2'
                            },
                            '&:focus': {
                                outline: 'none'
                            }
                        }}
                    >
                        Update
                    </Button>
                </DialogActions>
                </Box>
            </Dialog>

            {/* Export/Import Actions */}
            <Box sx={{
                mt: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport('csv')}
                    disabled={!currentProgramId}
                    sx={{
                        backgroundColor: 'rgba(33, 150, 243, 0.8)',
                        color: theme.palette.text.primary,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '200px', sm: '140px' },
                        '&:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 1)',
                        },
                        '&:disabled': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                            color: theme.palette.action.disabled,
                        }
                    }}
                >
                    Export CSV
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PDFIcon />}
                    onClick={() => handleExport('pdf')}
                    disabled={!currentProgramId}
                    sx={{
                        backgroundColor: 'rgba(244, 67, 54, 0.8)',
                        color: theme.palette.text.primary,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '200px', sm: '140px' },
                        '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 1)',
                        },
                        '&:disabled': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                            color: theme.palette.action.disabled,
                        }
                    }}
                >
                    Export PDF
                </Button>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={!currentProgramId}
                    sx={{
                        backgroundColor: 'rgba(255, 152, 0, 0.8)',
                        color: theme.palette.text.primary,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '200px', sm: '140px' },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 152, 0, 1)',
                        },
                        '&:disabled': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                            color: theme.palette.action.disabled,
                        }
                    }}
                >
                    Import Tasks
                    <input
                        type="file"
                        hidden
                        accept=".csv,.xlsx,.xls"
                        onChange={handleImport}
                        disabled={!currentProgramId}
                    />
                </Button>
            </Box>

            {/* Add Checklist Dialog */}
            {openAddDialog && (() => {
                console.log('🚀 Rendering Add Checklist Dialog');
                console.log('🚀 Dialog openAddDialog state:', openAddDialog);
                return null;
            })()}
            {openAddDialog && (
                <Dialog
                    open={openAddDialog}
                    onClose={() => {
                        console.log('🚀 Dialog close button clicked');
                        handleAddDialogClose();
                    }}
                    maxWidth="sm"
                    fullWidth
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            maxHeight: 'calc(100vh - 120px)',
                            marginTop: '80px',
                            marginBottom: '40px',
                            marginLeft: '32px',
                            marginRight: '32px'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: '1.25rem',
                        fontWeight: 'bold'
                    }}>
                        ➕ Add New Task
                    </DialogTitle>
                    <Box component="form" onSubmit={handleAddChecklistSubmit(handleAddChecklist)}>
                    <DialogContent sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        // Global styles for all form fields in dark mode
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover fieldset': {
                                borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                            '&.Mui-focused': {
                                color: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            },
                        },
                        // Calendar icon styling for date inputs
                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                            filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                            cursor: 'pointer',
                        },
                    }}>
                        <Typography variant="body2" sx={{
                            color: theme.palette.text.secondary,
                            mb: 2,
                            mt: 2,
                            fontStyle: 'italic'
                        }}>
                            Fill in the details to create a new task.
                        </Typography>
                            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                            <Controller
                                name="taskName"
                                control={addChecklistControl}
                                rules={{}}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Task Name"
                                        placeholder="Enter task name"
                                        error={!!addChecklistErrors.taskName}
                                        helperText={addChecklistErrors.taskName?.message}
                                        required
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="toolsResources"
                                control={addChecklistControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Tools/Resources"
                                        placeholder="Enter tools or resources needed"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="mode"
                                control={addChecklistControl}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true} sx={{
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        }}>Mode</InputLabel>
                                        <Select
                                            {...field}
                                            label="Mode"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            <MenuItem value="Online">Online</MenuItem>
                                            <MenuItem value="In-person">In-person</MenuItem>
                                            <MenuItem value="Hybrid">Hybrid</MenuItem>
                                            <MenuItem value="Remote">Remote</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                name="keyDeliverable"
                                control={addChecklistControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Key Deliverable"
                                        placeholder="Enter key deliverable"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="assignedTo"
                                control={addChecklistControl}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true} sx={{
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': {
                                                color: theme.palette.text.primary,
                                            },
                                        }}>Assigned To</InputLabel>
                                        <Select
                                            {...field}
                                            label="Assigned To"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {users.map((user) => (
                                                <MenuItem key={user.id} value={user.email}>
                                                    {user.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                name="scheduleDate"
                                control={addChecklistControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Schedule Date"
                                        type="date"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="notes"
                                control={addChecklistControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Notes"
                                        placeholder="Enter any additional notes"
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{
                        backgroundColor: theme.palette.background.default,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        padding: '16px 24px'
                    }}>
                        <Button
                            onClick={handleAddDialogClose}
                            variant="outlined"
                            sx={{
                                color: theme.palette.text.primary,
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                                borderWidth: 1.5,
                                '&:hover': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                    borderWidth: 1.5
                                },
                                '&:focus': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                    outline: 'none'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={addChecklistLoading}
                            onClick={() => {
                                console.log('🚀 Create button clicked');
                                console.log('🚀 addChecklistLoading:', addChecklistLoading);
                                console.log('🚀 Form errors:', addChecklistErrors);

                                // Get form values directly
                                const formValues = addChecklistControl._formValues;
                                console.log('🚀 Form values:', formValues);

                                // Call handleAddChecklist directly
                                console.log('🚀 Calling handleAddChecklist directly');
                                handleAddChecklist(formValues);
                            }}
                            startIcon={addChecklistLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                            sx={{
                                backgroundColor: '#2196F3',
                                color: '#ffffff',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: '#1976D2'
                                },
                                '&:focus': {
                                    outline: 'none'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#2196F3',
                                    color: '#ffffff',
                                    opacity: 0.4
                                }
                            }}
                        >
                            {addChecklistLoading ? 'Creating...' : 'Create'}
                        </Button>
                    </DialogActions>
                    </Box>
                </Dialog>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={() => {
                    setError(null);
                    setSuccess(null);
                }}
            >
                <Alert
                    onClose={() => {
                        setError(null);
                        setSuccess(null);
                    }}
                    severity={error ? 'error' : 'success'}
                >
                    {error || success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TaskManagement;
